import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Checkbox,
  Collapse,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { EditIcon } from 'components/CustomSVG';
import { TextFormattedNumber } from 'components';
import { singleUnitName } from 'helpers/other';
import { imgBaseUrl } from 'configs';
import round from 'helpers/mathRound';
import { getAlleDiscount, statusActivityNames } from 'pages/quotes/helpers';
import { getDiscount } from 'pages/quotes/utils/getDiscount';
import { setSelectedDraftedQuote } from 'redux/actions/quote';
import logo from '../../../../components/Logo/logo-dermpro.svg';
import { useStyles } from '../styles';
import EditQuoteNameDialog from './EditQuoteNameDialog';


const PatientActivityTableRow = ({
  quote,
  selectedTab,
  headTableCells
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [loaded, setLoaded] = useState(false);
  const [openEditQuoteName, setOpenEditQuoteName] = useState(false);

  const { id } = useParams<{ id: string }>();
  const {
    selectedDraftedQuote
  } = useSelector((state: IReduxState) => state.quote);
  const { stores = [] } = useSelector((state: IReduxState) => state.user.profile);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  const columns = headTableCells.reduce((acc, column) => {
    acc[column.label] = column;

    return acc;
  }, {});

  const {
    name,
    isTreatment,
    _id,
    selectedItems,
    products,
    createdBy: {
      firstName,
      lastName
    },
    createdAt,
    status = '',
    quoteDiscount
  } = quote || {};

  const [openRow, setOpenRow] = useState(false);
  const [totalsUpdated, setTotalsUpdated] = useState(false);
  const [totalsQuoteData, setTotalsQuoteData] = useState({
    [_id]: {
      subtotal: 0,
      quote_discount: 0,
      discount: 0,
      amount: 0
    }
  });

  useEffect(() => {
    const calculateQuoteDiscount = () => {
      if (quoteDiscount?.price_type && quoteDiscount?.price) {
        const { price_type, price } = quoteDiscount;
        return price_type === 'fixed' ? Math.abs(price) : Math.abs(price * (totalsQuoteData[_id]?.subtotal || 0) / 100);
      }

      return 0;
    };

    const quote_discount = calculateQuoteDiscount();
    const updatedTotals = {
      _id,
      subtotal: totalsQuoteData[_id]?.subtotal || 0,
      discount: totalsQuoteData[_id]?.discount || 0,
      quote_discount,
      amount: (totalsQuoteData[_id]?.amount || 0) - quote_discount,
    };

    if (totalsQuoteData[_id] && JSON.stringify(totalsQuoteData[_id]) !== JSON.stringify(updatedTotals)) {
      setTotalsQuoteData({ [_id]: updatedTotals });
      setTotalsUpdated(true);
    }
  }, [_id]);

  const quoteName = name ? name : 'N/A';
  const itemCreatedBy = firstName && lastName ? `${firstName} ${lastName}` : 'N/A';
  const quoteDateCreated = createdAt ? format(new Date(createdAt), 'MM/dd/yyyy') : '';
  const quoteStatus = status ? statusActivityNames[status] : statusActivityNames['7'];

  const handleItemSelect = (itemId) => {
    if (itemId === selectedDraftedQuote?._id) {
      dispatch(setSelectedDraftedQuote({}));
    } else {
      dispatch(setSelectedDraftedQuote({
        _id: itemId,
        subtotal: totalsQuoteData[itemId]?.subtotal,
        discount: totalsQuoteData[itemId]?.discount,
        quote_discount: totalsQuoteData[itemId]?.quote_discount,
        amount: totalsQuoteData[itemId]?.amount
      }));
    }
  };

  const handleItemEdit = (quote) => {
    dispatch(setSelectedDraftedQuote({}));
    history.push(`/quoting/profile/${id}/quote-workspace/patient-activity/${quote._id}/edit`);
  };

  return (
    <Fragment>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            checked={_id === selectedDraftedQuote?._id}
            color="primary"
            onChange={() => handleItemSelect(_id)}
          />
        </TableCell>

        {columns['Quote Name']?.display &&
          <TableCell align="center" className="font-bold">
            {quoteName}
            <IconButton
              title="Edit Quote Name"
              onClick={() => setOpenEditQuoteName(true)}
            >
              <EditIcon />
            </IconButton>
            <EditQuoteNameDialog
              isOpen={openEditQuoteName}
              onClose={setOpenEditQuoteName}
              quote={quote}
            />
          </TableCell>
        }
        {columns['Quote Type']?.display && <TableCell align="center">{isTreatment ? 'Treatment Plan' : 'Non-Treatment Plan'}</TableCell>}
        {columns['Created By']?.display && <TableCell className="whitespace-no-wrap" align="center">{itemCreatedBy}</TableCell>}
        {columns['Date Created']?.display &&
          <TableCell align="center">
            {quoteDateCreated}
          </TableCell>
        }

        {columns['Quote Discount']?.display &&
        <TableCell align="center">
          <NumberFormat
            value={totalsQuoteData[_id]?.quote_discount}
            displayType="text"
            thousandSeparator={','}
            decimalSeparator={'.'}
            prefix={'$'}
            fixedDecimalScale
            decimalScale={2}
          />
        </TableCell>
        }
        {columns['Total']?.display &&
          <TableCell align="center">
            <NumberFormat
              value={totalsQuoteData[_id]?.amount}
              displayType="text"
              thousandSeparator={','}
              decimalSeparator={'.'}
              prefix={'$'}
              fixedDecimalScale
              decimalScale={2}
            />
          </TableCell>
        }

        {columns['Status']?.display &&
          <TableCell align="center">
            <span className={`whitespace-no-wrap ${status} ${status ? 'indicator' : null}`}>
              {quoteStatus?.title}
            </span>
          </TableCell>
        }

        <TableCell align="center">
          <div className="flex items-center justify-center">
            {selectedTab === 'draft_patient_history' && (
              <IconButton
                title="Edit Quote"
                onClick={() => handleItemEdit(quote)}
              >
                <Icon>edit</Icon>
              </IconButton>
            )}
            <IconButton
              title={openRow ?  'Show Less' : 'Show More'}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpenRow(!openRow);
              }}
            >
              <Icon>{openRow ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}</Icon>
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className="p-0" colSpan={12}>
          <Collapse in={openRow} timeout="auto" unmountOnExit>
            <Box margin={0}>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell width="80px">ITEMS</TableCell>
                    <TableCell width="30%" />
                    <TableCell align="center">QUANTITY</TableCell>
                    <TableCell align="center">SUBTOTAL</TableCell>
                    <TableCell align="center">SAVINGS</TableCell>
                    <TableCell align="center">TOTAL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedItems.map((item, index) => {
                    const productInfo = products[item.sku]?.product;

                    if (!productInfo) {
                      return null;
                    }

                    const {
                      name,
                      price,
                      image,
                      catalog_product_unit = 'units',
                      subscription_active,
                      subscription_label,
                      quotepro_discounts,
                      subscription_options,
                      savings,
                      short_description
                    } = productInfo;

                    const {
                      sku,
                      qty,
                      price: updatedPrice,
                      product_option: {
                        extension_attributes: {
                          subscription_id,
                          quotepro_discounts: customQuoteproDiscounts
                        },
                      },
                      custom_subscription_price
                    } = item;

                    const itemPrice = updatedPrice || price;
                    const savingsValue = round(Number(savings)) || 0;
                    const unitsName = catalog_product_unit || 'units';

                    const dataForDiscount = {
                      savings: savingsValue,
                      discount_options: quotepro_discounts && quotepro_discounts !== '[]' ? JSON.parse(quotepro_discounts) : [customQuoteproDiscounts],
                      itemPrice
                    };

                    const isAlleSKU = sku.includes('bdsku');

                    const discount = isAlleSKU
                      ? round(getAlleDiscount(products[item.sku].product)) || 0
                      : round(getDiscount(item, dataForDiscount)) || 0;

                    const subscriptionData = subscription_options?.find((s) => s.id === subscription_id);
                    const isNullPrice = subscriptionData?.installment_price === null && subscriptionData?.adjustment_price === null;

                    const regularPrice = !!subscriptionData && subscription_active === '1' && !isNullPrice && updatedPrice === 0
                      ? round(subscriptionData.installment_price + subscriptionData.adjustment_price)
                      : itemPrice || 0;

                    const subtotal = regularPrice * qty;

                    const amount = savingsValue && !updatedPrice
                      ? (regularPrice * qty)
                      : (regularPrice * qty) - discount;

                    if (!totalsUpdated) {
                      const totalsForItem = {
                        subtotal,
                        discount,
                        amount
                      };

                      setTotalsQuoteData((prevTotals) => ({
                        ...prevTotals,
                        [_id]: {
                          subtotal: prevTotals[_id].subtotal + totalsForItem.subtotal,
                          discount: prevTotals[_id].discount + totalsForItem.discount,
                          quote_discount: prevTotals[_id].quote_discount,
                          amount: prevTotals[_id].amount + totalsForItem.amount
                        }
                      }));

                      setTotalsUpdated(true);
                    }

                    return (
                      <TableRow key={`${index}-${item._id}`}>
                        <TableCell component="th" scope="row">
                          <img
                            src={loaded ? `${mediaStoreUrl}${image}` : logo}
                            alt={`${name} image`}
                            className={classes.patientActivityTableImage}
                            onLoad={() => setLoaded(true)}
                            onError={(e) => {
                              (e.target as HTMLImageElement).onerror = null;
                              (e.target as HTMLImageElement).src = logo;
                            }}
                          />
                        </TableCell>
                        <TableCell width="50%">
                          <Typography variant="subtitle2" className="font-semibold" gutterBottom>
                            {name}&nbsp;
                            <span className="text-13 font-italic font-medium whitespace-no-wrap">
                              (<TextFormattedNumber value={round(updatedPrice ? updatedPrice : itemPrice - savingsValue)}  prefix="$" />/
                              {singleUnitName(unitsName)})
                            </span>
                          </Typography>
                          {subscription_active === '1' && !!subscriptionData?.frequency_count &&
                          <>
                            <div className="flex flex-wrap">
                              <span className="text-13 whitespace-no-wrap mr-1 font-italic font-bold">
                                {subscription_label}{':'}
                              </span>
                              <span className="text-13 whitespace-no-wrap font-italic">
                                {subscriptionData?.sproduct_label || ''}
                              </span>
                            </div>
                            <div className="flex flex-wrap mb-2">
                              <span className="text-13 whitespace-no-wrap mr-1 font-italic font-bold">
                                {'Periodic Price:'}
                              </span>
                              <span className="text-13 whitespace-no-wrap font-italic">
                                <TextFormattedNumber
                                  value={custom_subscription_price
                                    ? custom_subscription_price
                                    : round(subscriptionData?.installment_price) || 0
                                  }
                                  prefix="$"
                                />
                              </span>
                            </div>
                          </>
                          }
                          <Typography dangerouslySetInnerHTML={{ __html: short_description || '' }} />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{`${qty} ${
                            qty === 1 ? singleUnitName(unitsName) : unitsName
                          }`}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <TextFormattedNumber value={subtotal} prefix="$" />
                        </TableCell>
                        <TableCell align="center">
                          <TextFormattedNumber value={discount} prefix="$" />
                        </TableCell>
                        <TableCell align="center">
                          <TextFormattedNumber value={isAlleSKU ? 0 : amount} prefix="$" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell align="center">
                      <TextFormattedNumber value={totalsQuoteData[_id]?.subtotal} prefix="$" />
                    </TableCell>
                    <TableCell align="center">
                      <TextFormattedNumber value={totalsQuoteData[_id]?.discount} prefix="$" />
                    </TableCell>
                    <TableCell align="center">
                      <TextFormattedNumber value={totalsQuoteData[_id]?.amount} prefix="$" />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default PatientActivityTableRow;
